import { useState } from "react";
import ModalForm from "../../../../components/ModalForm";
import AlertMessage, { AlertLevel } from "../../../../components/AlertMessage";
import { DistrictCreateEditModel, useCreateDistrictMutation } from "../../districtsApiSlice";
import PrimaryDistrictInfoSection from "../sections/PrimaryDistrictInfoSection";
import DistrictAddressInfoSection from "../sections/DistrictAddressInfoSection";

interface AddDistrictProps {
	addDistrictFormIsOpen: boolean;
	setAddDistrictFormIsOpen: (value: boolean) => void;
}

const initialModel: DistrictCreateEditModel = {
	id: 0,
	name: "",
	state: "",
	address: "",
	city: "",
	zip: "",
	phone: "",
	code: "",
	emailAddress: "",
	externalID: "",
	cleverID: "",
	showCleverId: false,
	classLinkID: "",
	showClassLinkId: false,
	disableUserImages: false,
	isIncludedInReports: true,
	takenSeats: 0,
	totalSeats: 0,
};

export default function AddDistrict({
	addDistrictFormIsOpen,
	setAddDistrictFormIsOpen,
}: AddDistrictProps) {
	const [isSaving, setIsSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [model, setModel] = useState<DistrictCreateEditModel>(initialModel);
	const [showAddressSection, setShowAddressSection] = useState(true);

	function toggleShowAddressSection() {
		setShowAddressSection((val) => !val);
	}

	const [addDistrict] = useCreateDistrictMutation();

	const setPrimaryInfo = (
		patch: Partial<Pick<DistrictCreateEditModel, "name" | "code" | "externalID">>
	) => setModel((s) => ({ ...s, ...patch }));

	const setAddressInfo = (
		patch: Partial<
			Pick<
				DistrictCreateEditModel,
				"state" | "address" | "city" | "zip" | "emailAddress" | "phone"
			>
		>
	) => setModel((s) => ({ ...s, ...patch }));

	const setSettingsInfo = (
		patch: Partial<Pick<DistrictCreateEditModel, "disableUserImages" | "isIncludedInReports">>
	) => setModel((s) => ({ ...s, ...patch }));

	const hasEmptyRequiredFields = !model.name || !model.code;
	const canSubmit = !isSaving && !hasEmptyRequiredFields;

	const handleSubmit = () => {
		setIsSaving(true);

		if (!hasEmptyRequiredFields) {
			addDistrict(model)
				.unwrap()
				.then(() => {
					onModalClosed();
				})
				.catch((error) => {
					setSaveError(error.data);
				});
		}

		setIsSaving(false);
	};

	const onCancel = () => {
		onModalClosed();
	};

	const onModalClosed = () => {
		setAddDistrictFormIsOpen(false);
		setModel(initialModel);
		setSaveError("");
	};

	return (
		<ModalForm
			open={addDistrictFormIsOpen}
			title={"Add New District"}
			form={
				<>
					<AlertMessage
						className="my-2"
						type={AlertLevel.Error}
						message={saveError}
						showIcon={true}
						onClear={() => setSaveError("")}
					/>
					<PrimaryDistrictInfoSection
						value={{
							name: model.name,
							code: model.code,
							externalID: model.externalID,
							disableUserImages: model.disableUserImages,
							isIncludedInReports: model.isIncludedInReports,
						}}
						onPrimaryInfoChange={setPrimaryInfo}
						onAddSettingsInfoChange={setSettingsInfo}
					/>
					<DistrictAddressInfoSection
						value={{
							address: model.address,
							city: model.city,
							state: model.state,
							zip: model.zip,
							emailAddress: model.emailAddress,
							phone: model.phone,
						}}
						onAddressChange={setAddressInfo}
						showAddressSection={showAddressSection}
						toggleShowAddressSection={toggleShowAddressSection}
					/>
				</>
			}
			confirmLabel="Save"
			disableConfirm={!canSubmit}
			onConfirm={handleSubmit}
			onCancel={onCancel}
		/>
	);
}
